import axios from 'axios'
import urlEncodeValues from '@/utils/url-encode-values'
import stringifyValues from '@/utils/stringify-values'
import { mayNotifySuccessResponse, mayNotifyErrorResponse } from './notification.service'

function throwError (error) {
  throw error
}

const httpClient = axios.create({
  baseURL: process.env.VUE_APP_IS_PUBLIC ? '/public-site/' : '/app/',
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  transformResponse (data) {
    if (!data) return
    try {
      return JSON.parse(data)
    } catch (err) {
      throwError(err)
    }
  },
  transformRequest (data, headers) {
    switch (headers['Content-Type']) {
      case 'application/x-www-form-urlencoded':
        return urlEncodeValues(data)
      case 'multipart/form-data':
        return data
      default:
        return JSON.stringify(data)
    }
  }
})

httpClient.interceptors.request.use(
  (request) => {
    return request
  },
  (error) => {
    Promise.reject(error)
  }
)

httpClient.interceptors.response.use(
  (res) => {
    mayNotifySuccessResponse(res)
    return res
  },
  (error) => {
    mayNotifyErrorResponse(error)
    throwError(error)
  }
)

async function httpRequest (
  url,
  method,
  { headers = {}, body, query, baseUrl, notifyErrorMsg = '', hasNotification, hasDefaultRedirect = true, timeout = 15000 } = {}
) {
  const queryParams = stringifyValues(query)
  const axiosOptions = {
    url,
    method,
    headers,
    data: body,
    params: queryParams,
    transformRequest: undefined,
    notifyErrorMsg,
    hasNotification,
    hasDefaultRedirect
  }

  try {
    if (baseUrl) {
      axiosOptions.baseURL = baseUrl
    }
    httpClient.defaults.timeout = timeout

    const response = await httpClient(axiosOptions)
    if (response) {
      return response
    }
  } catch (error) {
    console.error(error)
    const errors = error?.response?.data?.errors

    if (errors && typeof errors === 'object') {
    // Validation errors
      throwError(error)
    }
  }
}

export async function get (url, opts) {
  return await httpRequest(url, 'GET', opts)
}
export async function post (url, opts) {
  return await httpRequest(url, 'POST', opts)
}
export async function put (url, opts) {
  return await httpRequest(url, 'PUT', opts)
}
export async function patch (url, opts) {
  return await httpRequest(url, 'PATCH', opts)
}
export async function del (url, opts) {
  return await httpRequest(url, 'DELETE', opts)
}
export async function head (url, opts) {
  return await httpRequest(url, 'HEAD', opts)
}
export async function options (url, opts) {
  return await httpRequest(url, 'OPTIONS', opts)
}
export default {
  get,
  post,
  put,
  patch,
  delete: del,
  head,
  options,
  httpClient
}
